// engagement-components/JourneyList.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaEdit,
  FaCopy,
  FaTrash,
  FaPlay,
  FaPause,
  FaPlus,
  FaRoute,
  FaSearch,
  FaSyncAlt,
  FaFilter,
  FaBolt,
  FaCheckCircle,
  FaRegClock,
  FaChartLine,
  FaTimes,
} from "react-icons/fa";
import dayjs from "dayjs";
import axios from "axios";
import { apiUrl, authHeaders } from "./config";

const C = {
  brand: "#4f46e5",
  brandLt: "#eef2ff",
  green: "#059669",
  greenLt: "#ecfdf5",
  amber: "#d97706",
  amberLt: "#fffbeb",
  text: "#111827",
  muted: "#6b7280",
  subtle: "#9ca3af",
  border: "#e5e7eb",
  bg: "#f9fafb",
  white: "#ffffff",
  danger: "#ef4444",
  dangerLt: "#fef2f2",
  cyan: "#0891b2",
  cyanLt: "#ecfeff",
};

const TRIGGER_META = {
  Birthday: { emoji: "🎂", color: "#7c3aed", bg: "#f5f3ff" },
  Anniversary: { emoji: "💍", color: "#be185d", bg: "#fff1f2" },
  "Check-in": { emoji: "🏨", color: "#0369a1", bg: "#eff6ff" },
  "Check-out": { emoji: "👋", color: "#0f766e", bg: "#f0fdfa" },
  "Profile Incomplete": { emoji: "👤", color: "#d97706", bg: "#fffbeb" },
  Manual: { emoji: "⚡", color: "#6b7280", bg: "#f9fafb" },
  Custom: { emoji: "⚡", color: "#6b7280", bg: "#f9fafb" },
};

const STATUS_META = {
  Active: { bg: "#dcfce7", color: "#15803d", label: "Active" },
  Paused: { bg: "#f3f4f6", color: "#6b7280", label: "Paused" },
  Draft: { bg: "#fef9c3", color: "#92400e", label: "Draft" },
  Archived: { bg: "#fee2e2", color: "#991b1b", label: "Archived" },
};

const normalizeJourney = (j = {}) => ({
  ...j,
  id: j.journeyid || j.id,
  steps: Array.isArray(j.steps_data) ? j.steps_data.length : Number(j.steps || 0),
  trigger: j.trigger_type || j.trigger || "Custom",
  status: j.status || "Draft",
});

const compactNumber = (value) =>
  Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const StatusPill = ({ status }) => {
  const s = STATUS_META[status] || STATUS_META.Draft;

  return (
    <span className="eng-journey-status" style={{ "--status-bg": s.bg, "--status-color": s.color }}>
      {s.label || status}
    </span>
  );
};

const Metric = ({ label, value, tone }) => (
  <div className="eng-journey-metric">
    <strong style={{ color: tone }}>{compactNumber(value)}</strong>
    <span>{label}</span>
  </div>
);

const JourneyList = ({ onNavigate }) => {
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [confirmName, setConfirmName] = useState("");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [triggerFilter, setTriggerFilter] = useState("All");

  const fetchJourneys = useCallback(async (manualRefresh = false) => {
    if (manualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const res = await axios.get(`${apiUrl}/feedback/journeys`, authHeaders());
      const normalized = (res.data || []).map(normalizeJourney);
      setJourneys(normalized);
    } catch (error) {
      console.error("Error fetching journeys:", error);
      setJourneys([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchJourneys(false);
  }, [fetchJourneys]);

  const toggleStatus = async (j) => {
    const next = j.status === "Active" ? "Paused" : "Active";
    const id = j.journeyid || j.id;

    setJourneys((previous) =>
      previous.map((x) => ((x.journeyid || x.id) === id ? { ...x, status: next } : x))
    );

    try {
      await axios.put(
        `${apiUrl}/feedback/journeys/${id}`,
        {
          name: j.name,
          trigger: j.trigger_type || j.trigger,
          triggerConditions: j.trigger_conditions,
          timezone: j.timezone,
          status: next,
          steps_data: j.steps_data || [],
        },
        authHeaders()
      );
    } catch (error) {
      console.error("Error updating journey status:", error);
      fetchJourneys(false);
    }
  };

  const duplicate = async (j) => {
    const copy = {
      name: `${j.name} (Copy)`,
      trigger: j.trigger_type || j.trigger,
      triggerConditions: j.trigger_conditions,
      timezone: j.timezone,
      status: "Draft",
      steps_data: j.steps_data || [],
    };

    try {
      const res = await axios.post(`${apiUrl}/feedback/journeys`, copy, authHeaders());
      const saved = normalizeJourney(res.data ? { ...res.data, id: res.data.journeyid } : { ...copy, id: `j${Date.now()}` });
      setJourneys((previous) => [saved, ...previous]);
    } catch (error) {
      console.error("Error duplicating journey:", error);
      setJourneys((previous) => [normalizeJourney({ ...copy, id: `j${Date.now()}` }), ...previous]);
    }
  };

  const deleteJourney = async () => {
    if (!confirmId) return;

    setDeleting(confirmId);

    try {
      await axios.delete(`${apiUrl}/feedback/journeys/${confirmId}`, authHeaders());
    } catch (error) {
      console.error("Error deleting journey:", error);
    }

    setJourneys((previous) => previous.filter((x) => (x.journeyid || x.id) !== confirmId));
    setConfirmId(null);
    setConfirmName("");
    setDeleting(null);
  };

  const triggerOptions = useMemo(() => {
    const unique = Array.from(new Set(journeys.map((j) => j.trigger).filter(Boolean)));
    return ["All", ...unique.sort()];
  }, [journeys]);

  const filteredJourneys = useMemo(() => {
    const q = searchText.trim().toLowerCase();

    return journeys.filter((j) => {
      const matchesSearch =
        !q ||
        `${j.name || ""} ${j.trigger || ""} ${j.status || ""}`
          .toLowerCase()
          .includes(q);

      const matchesStatus = statusFilter === "All" || j.status === statusFilter;
      const matchesTrigger = triggerFilter === "All" || j.trigger === triggerFilter;

      return matchesSearch && matchesStatus && matchesTrigger;
    });
  }, [journeys, searchText, statusFilter, triggerFilter]);

  const stats = useMemo(() => {
    const active = journeys.filter((j) => j.status === "Active").length;
    const draft = journeys.filter((j) => j.status === "Draft").length;
    const paused = journeys.filter((j) => j.status === "Paused").length;
    const totalFired = journeys.reduce((sum, j) => sum + Number(j.total_fired || 0), 0);
    const totalDelivered = journeys.reduce((sum, j) => sum + Number(j.total_delivered || 0), 0);

    return {
      total: journeys.length,
      active,
      draft,
      paused,
      totalFired,
      totalDelivered,
    };
  }, [journeys]);

  const clearFilters = () => {
    setSearchText("");
    setStatusFilter("All");
    setTriggerFilter("All");
  };

  if (loading) {
    return (
      <div className="eng-journey-root">
        <style>{journeyStyles}</style>
        <div className="eng-journey-loader">
          <div className="eng-journey-dots">
            <span />
            <span />
            <span />
            <span />
          </div>
          <strong>Loading guest journeys...</strong>
          <p>Preparing automation sequences and performance metrics.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="eng-journey-root">
      <style>{journeyStyles}</style>

      <section className="eng-journey-hero">
        <div className="eng-journey-hero-left">
          <span className="eng-journey-hero-icon">
            <FaRoute />
          </span>

          <div>
            <div className="eng-journey-eyebrow">Guest Journey Automation</div>
            <h2>Journeys</h2>
            <p>Design and manage automated, trigger-based guest communication sequences across the stay lifecycle.</p>
          </div>
        </div>

        <div className="eng-journey-hero-actions">
          <button
            type="button"
            className="eng-journey-soft-btn"
            onClick={() => fetchJourneys(true)}
            disabled={refreshing}
          >
            <FaSyncAlt className={refreshing ? "eng-spin" : ""} />
            {refreshing ? "Refreshing" : "Refresh"}
          </button>

          <button
            type="button"
            className="eng-journey-primary-btn"
            onClick={() => onNavigate("journeys-create")}
          >
            <FaPlus />
            New Journey
          </button>
        </div>
      </section>

      <section className="eng-journey-stat-grid">
        <article className="eng-journey-stat" style={{ "--tone": C.brand }}>
          <span>Total Journeys</span>
          <strong>{compactNumber(stats.total)}</strong>
        </article>
        <article className="eng-journey-stat" style={{ "--tone": C.green }}>
          <span>Active</span>
          <strong>{compactNumber(stats.active)}</strong>
        </article>
        <article className="eng-journey-stat" style={{ "--tone": C.amber }}>
          <span>Draft / Paused</span>
          <strong>{compactNumber(stats.draft + stats.paused)}</strong>
        </article>
        <article className="eng-journey-stat" style={{ "--tone": C.cyan }}>
          <span>Total Fired</span>
          <strong>{compactNumber(stats.totalFired)}</strong>
        </article>
        <article className="eng-journey-stat" style={{ "--tone": "#7c3aed" }}>
          <span>Total Delivered</span>
          <strong>{compactNumber(stats.totalDelivered)}</strong>
        </article>
      </section>

      <section className="eng-journey-toolbar">
        <div className="eng-journey-search">
          <FaSearch />
          <input
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search journey name, trigger, or status..."
          />
        </div>

        <div className="eng-journey-filter-group">
          <span className="eng-journey-filter-label">
            <FaFilter />
            Filters
          </span>

          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            {["All", "Active", "Paused", "Draft", "Archived"].map((status) => (
              <option key={status} value={status}>
                {status === "All" ? "All Status" : status}
              </option>
            ))}
          </select>

          <select value={triggerFilter} onChange={(event) => setTriggerFilter(event.target.value)}>
            {triggerOptions.map((trigger) => (
              <option key={trigger} value={trigger}>
                {trigger === "All" ? "All Triggers" : trigger}
              </option>
            ))}
          </select>

          {(searchText || statusFilter !== "All" || triggerFilter !== "All") && (
            <button type="button" className="eng-journey-clear-btn" onClick={clearFilters}>
              Clear
            </button>
          )}
        </div>
      </section>

      {journeys.length === 0 ? (
        <section className="eng-journey-empty">
          <span className="eng-journey-empty-icon">
            <FaRoute />
          </span>
          <strong>No journeys yet</strong>
          <p>
            Create automated sequences that send birthday wishes, check-in greetings, post-checkout feedback requests,
            and more.
          </p>
          <button type="button" className="eng-journey-primary-btn" onClick={() => onNavigate("journeys-create")}>
            <FaPlus />
            Create Your First Journey
          </button>
        </section>
      ) : filteredJourneys.length === 0 ? (
        <section className="eng-journey-empty">
          <span className="eng-journey-empty-icon">
            <FaSearch />
          </span>
          <strong>No journeys match the filters</strong>
          <p>Adjust search, trigger, or status filters to view more journeys.</p>
          <button type="button" className="eng-journey-soft-btn" onClick={clearFilters}>
            Clear Filters
          </button>
        </section>
      ) : (
        <section className="eng-journey-grid">
          {filteredJourneys.map((j) => {
            const meta = TRIGGER_META[j.trigger] || TRIGGER_META.Custom;
            const id = j.journeyid || j.id;
            const active = j.status === "Active";
            const deliveredRate = j.total_fired ? Math.round((Number(j.total_delivered || 0) / Number(j.total_fired || 1)) * 100) : 0;
            const lastUpdated = j.updated_at || j.created_at || j.createdon || j.created_at;

            return (
              <article
                key={id}
                className={`eng-journey-card ${active ? "is-active" : ""}`}
                style={{ "--trigger-color": meta.color, "--trigger-bg": meta.bg }}
              >
                <div className="eng-journey-card-top">
                  <span className="eng-journey-trigger-icon">{meta.emoji}</span>
                  <StatusPill status={j.status} />
                </div>

                <div className="eng-journey-card-title-row">
                  <h3 title={j.name}>{j.name || "Untitled Journey"}</h3>
                </div>

                <div className="eng-journey-meta-row">
                  <span className="eng-journey-trigger-pill">
                    <FaBolt />
                    {j.trigger || "Custom"}
                  </span>
                  <span>{j.steps} step{j.steps !== 1 ? "s" : ""}</span>
                </div>

                <div className="eng-journey-metrics">
                  <Metric label="Fired" value={j.total_fired || 0} tone={C.text} />
                  <Metric label="Delivered" value={j.total_delivered || 0} tone={C.green} />
                  <Metric label="Clicked" value={j.total_clicks || 0} tone={C.brand} />
                </div>

                <div className="eng-journey-foot">
                  <span>
                    <FaChartLine />
                    {deliveredRate}% delivery
                  </span>
                  <span>
                    <FaRegClock />
                    {lastUpdated ? dayjs(lastUpdated).format("DD MMM") : "Not updated"}
                  </span>
                </div>

                <div className="eng-journey-actions" onClick={(event) => event.stopPropagation()}>
                  <button
                    type="button"
                    title="Edit journey"
                    onClick={() => onNavigate("journeys-edit", j)}
                    className="eng-journey-icon-btn brand"
                  >
                    <FaEdit />
                  </button>

                  <button
                    type="button"
                    title={active ? "Pause journey" : "Activate journey"}
                    onClick={() => toggleStatus(j)}
                    className={`eng-journey-icon-btn ${active ? "amber" : "green"}`}
                  >
                    {active ? <FaPause /> : <FaPlay />}
                  </button>

                  <button
                    type="button"
                    title="Duplicate journey"
                    onClick={() => duplicate(j)}
                    className="eng-journey-icon-btn cyan"
                  >
                    <FaCopy />
                  </button>

                  <button
                    type="button"
                    title="Delete journey"
                    onClick={() => {
                      setConfirmId(id);
                      setConfirmName(j.name || "Untitled Journey");
                    }}
                    className="eng-journey-icon-btn danger"
                  >
                    <FaTrash />
                  </button>
                </div>
              </article>
            );
          })}
        </section>
      )}

      {confirmId && (
        <div className="eng-journey-delete-root" role="dialog" aria-modal="true" aria-label="Delete journey">
          <button
            type="button"
            className="eng-journey-delete-backdrop"
            onClick={() => {
              setConfirmId(null);
              setConfirmName("");
            }}
            aria-label="Cancel delete"
          />

          <div className="eng-journey-delete-card">
            <div className="eng-journey-delete-icon">
              <FaTrash />
            </div>

            <div className="eng-journey-delete-copy">
              <strong>Delete Journey?</strong>
              <p>
                <b>{confirmName}</b> will be permanently deleted. All scheduled messages for this journey will stop
                firing.
              </p>
            </div>

            <div className="eng-journey-delete-actions">
              <button
                type="button"
                className="eng-journey-soft-btn"
                onClick={() => {
                  setConfirmId(null);
                  setConfirmName("");
                }}
                disabled={!!deleting}
              >
                <FaTimes />
                Cancel
              </button>

              <button
                type="button"
                className="eng-journey-danger-btn"
                onClick={deleteJourney}
                disabled={!!deleting}
              >
                {deleting ? "Deleting..." : "Delete Journey"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const journeyStyles = `
  .eng-journey-root {
    min-height: 100%;
    padding: 18px 22px 28px;
    background:
      radial-gradient(circle at 7% 0%, rgba(79, 70, 229, 0.08), transparent 28%),
      radial-gradient(circle at 92% 3%, rgba(5, 150, 105, 0.08), transparent 30%),
      #f8fafc;
    color: ${C.text};
  }

  .eng-journey-hero {
    background:
      radial-gradient(circle at top left, rgba(79, 70, 229, 0.14), transparent 34%),
      radial-gradient(circle at top right, rgba(5, 150, 105, 0.10), transparent 32%),
      linear-gradient(135deg, #ffffff 0%, #fbfdff 100%);
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-radius: 24px;
    box-shadow: 0 16px 42px rgba(17, 24, 39, 0.075);
    padding: 17px 18px;
    margin-bottom: 12px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
  }

  .eng-journey-hero-left {
    display: flex;
    align-items: flex-start;
    gap: 13px;
    min-width: 0;
  }

  .eng-journey-hero-icon,
  .eng-journey-empty-icon {
    width: 52px;
    height: 52px;
    border-radius: 19px;
    background: linear-gradient(135deg, #4f46e5, #6366f1);
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 16px 34px rgba(79, 70, 229, 0.22);
    flex: 0 0 auto;
    font-size: 1.25rem;
  }

  .eng-journey-eyebrow {
    color: #4f46e5;
    font-size: 0.72rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    font-weight: 950;
    margin-bottom: 5px;
  }

  .eng-journey-hero h2 {
    color: #111827;
    font-size: 1.42rem;
    line-height: 1.16;
    font-weight: 950;
    margin: 0;
  }

  .eng-journey-hero p {
    color: #64748b;
    font-size: 0.82rem;
    line-height: 1.5;
    font-weight: 760;
    margin: 7px 0 0;
    max-width: 780px;
  }

  .eng-journey-hero-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
  }

  .eng-journey-primary-btn,
  .eng-journey-soft-btn,
  .eng-journey-danger-btn {
    min-height: 40px;
    border-radius: 14px;
    border: 1px solid rgba(17, 24, 39, 0.10);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0 15px;
    font-size: 0.82rem;
    font-weight: 950;
    cursor: pointer;
    white-space: nowrap;
    transition: transform 0.16s ease, box-shadow 0.16s ease, opacity 0.16s ease;
  }

  .eng-journey-primary-btn {
    background: linear-gradient(135deg, #4f46e5, #6366f1);
    border-color: #4f46e5;
    color: #ffffff;
    box-shadow: 0 14px 30px rgba(79, 70, 229, 0.18);
  }

  .eng-journey-soft-btn {
    background: #ffffff;
    color: #334155;
  }

  .eng-journey-danger-btn {
    background: linear-gradient(135deg, #dc2626, #ef4444);
    border-color: #dc2626;
    color: #ffffff;
    box-shadow: 0 14px 30px rgba(220, 38, 38, 0.18);
  }

  .eng-journey-primary-btn:hover,
  .eng-journey-soft-btn:hover,
  .eng-journey-danger-btn:hover {
    transform: translateY(-1px);
  }

  .eng-journey-primary-btn:disabled,
  .eng-journey-soft-btn:disabled,
  .eng-journey-danger-btn:disabled {
    opacity: 0.62;
    cursor: not-allowed;
    transform: none;
  }

  .eng-spin {
    animation: eng-spin 0.9s linear infinite;
  }

  @keyframes eng-spin {
    to { transform: rotate(360deg); }
  }

  .eng-journey-stat-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 12px;
  }

  .eng-journey-stat {
    background:
      radial-gradient(circle at top right, color-mix(in srgb, var(--tone) 12%, transparent), transparent 36%),
      #ffffff;
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-top: 4px solid var(--tone);
    border-radius: 20px;
    box-shadow: 0 12px 30px rgba(17, 24, 39, 0.06);
    padding: 13px 14px;
    min-width: 0;
  }

  .eng-journey-stat span {
    color: #64748b;
    display: block;
    font-size: 0.66rem;
    font-weight: 950;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-bottom: 7px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .eng-journey-stat strong {
    color: #0f172a;
    display: block;
    font-size: 1.24rem;
    line-height: 1.12;
    font-weight: 950;
  }

  .eng-journey-toolbar {
    background: #ffffff;
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-radius: 22px;
    box-shadow: 0 12px 30px rgba(17, 24, 39, 0.06);
    padding: 12px;
    margin-bottom: 12px;
    display: grid;
    grid-template-columns: minmax(260px, 1fr) auto;
    gap: 10px;
    align-items: center;
  }

  .eng-journey-search {
    min-height: 42px;
    border-radius: 15px;
    border: 1px solid rgba(17, 24, 39, 0.10);
    background: #ffffff;
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 0 12px;
  }

  .eng-journey-search svg {
    color: #94a3b8;
    flex: 0 0 auto;
  }

  .eng-journey-search input {
    width: 100%;
    min-height: 40px;
    border: 0;
    outline: none;
    color: #0f172a;
    font-size: 0.84rem;
    font-weight: 760;
  }

  .eng-journey-search input::placeholder {
    color: #94a3b8;
  }

  .eng-journey-filter-group {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .eng-journey-filter-label {
    min-height: 38px;
    border-radius: 999px;
    background: #f8fafc;
    color: #64748b;
    border: 1px solid rgba(17, 24, 39, 0.08);
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 0 11px;
    font-size: 0.74rem;
    font-weight: 950;
  }

  .eng-journey-filter-group select {
    min-height: 40px;
    min-width: 142px;
    border-radius: 14px;
    border: 1px solid rgba(17, 24, 39, 0.10);
    background: #ffffff;
    color: #0f172a;
    padding: 0 10px;
    font-size: 0.82rem;
    font-weight: 850;
    outline: none;
  }

  .eng-journey-clear-btn {
    min-height: 40px;
    border-radius: 14px;
    border: 1px solid rgba(17, 24, 39, 0.10);
    background: #fff;
    color: #4f46e5;
    padding: 0 12px;
    font-size: 0.78rem;
    font-weight: 950;
    cursor: pointer;
  }

  .eng-journey-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(286px, 1fr));
    gap: 12px;
  }

  .eng-journey-card {
    background:
      radial-gradient(circle at top right, color-mix(in srgb, var(--trigger-color) 8%, transparent), transparent 35%),
      #ffffff;
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-top: 4px solid rgba(148, 163, 184, 0.9);
    border-radius: 22px;
    box-shadow: 0 12px 30px rgba(17, 24, 39, 0.06);
    padding: 14px;
    min-height: 250px;
    display: flex;
    flex-direction: column;
    gap: 11px;
    transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
  }

  .eng-journey-card.is-active {
    border-top-color: #4f46e5;
    border-color: #c7d2fe;
  }

  .eng-journey-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 42px rgba(17, 24, 39, 0.11);
  }

  .eng-journey-card-top,
  .eng-journey-card-title-row,
  .eng-journey-meta-row,
  .eng-journey-foot,
  .eng-journey-actions {
    display: flex;
    align-items: center;
  }

  .eng-journey-card-top,
  .eng-journey-card-title-row,
  .eng-journey-foot {
    justify-content: space-between;
    gap: 10px;
  }

  .eng-journey-trigger-icon {
    width: 44px;
    height: 44px;
    border-radius: 16px;
    background: var(--trigger-bg);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.28rem;
    flex: 0 0 auto;
  }

  .eng-journey-status {
    min-height: 28px;
    border-radius: 999px;
    background: var(--status-bg);
    color: var(--status-color);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 10px;
    font-size: 0.68rem;
    font-weight: 950;
    white-space: nowrap;
  }

  .eng-journey-card h3 {
    color: #111827;
    font-size: 0.96rem;
    line-height: 1.28;
    font-weight: 950;
    margin: 0;
    min-height: 38px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .eng-journey-meta-row {
    gap: 7px;
    color: #64748b;
    font-size: 0.74rem;
    font-weight: 800;
    flex-wrap: wrap;
  }

  .eng-journey-trigger-pill {
    min-height: 24px;
    border-radius: 999px;
    background: var(--trigger-bg);
    color: var(--trigger-color);
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 8px;
    font-size: 0.68rem;
    font-weight: 950;
  }

  .eng-journey-metrics {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    border-top: 1px solid rgba(17, 24, 39, 0.08);
    border-bottom: 1px solid rgba(17, 24, 39, 0.08);
    padding: 10px 0;
    gap: 0;
  }

  .eng-journey-metric {
    text-align: center;
    border-left: 1px solid rgba(17, 24, 39, 0.08);
    min-width: 0;
  }

  .eng-journey-metric:first-child {
    border-left: 0;
  }

  .eng-journey-metric strong {
    display: block;
    font-size: 1rem;
    line-height: 1.1;
    font-weight: 950;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .eng-journey-metric span {
    display: block;
    color: #94a3b8;
    font-size: 0.58rem;
    font-weight: 950;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-top: 4px;
  }

  .eng-journey-foot {
    color: #64748b;
    font-size: 0.68rem;
    font-weight: 850;
    margin-top: auto;
  }

  .eng-journey-foot span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  .eng-journey-actions {
    gap: 7px;
    margin-top: 2px;
  }

  .eng-journey-icon-btn {
    flex: 1;
    min-height: 36px;
    border-radius: 12px;
    border: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.15s ease, opacity 0.15s ease;
  }

  .eng-journey-icon-btn:hover {
    transform: translateY(-1px);
  }

  .eng-journey-icon-btn.brand { background: #eef2ff; color: #4f46e5; }
  .eng-journey-icon-btn.green { background: #ecfdf5; color: #059669; }
  .eng-journey-icon-btn.amber { background: #fffbeb; color: #d97706; }
  .eng-journey-icon-btn.cyan { background: #ecfeff; color: #0891b2; }
  .eng-journey-icon-btn.danger { background: #fef2f2; color: #ef4444; }

  .eng-journey-empty,
  .eng-journey-loader {
    min-height: 360px;
    background: #ffffff;
    border: 1px dashed #cbd5e1;
    border-radius: 24px;
    box-shadow: 0 16px 42px rgba(17, 24, 39, 0.06);
    padding: 40px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    text-align: center;
  }

  .eng-journey-empty strong,
  .eng-journey-loader strong {
    color: #111827;
    font-size: 1.02rem;
    font-weight: 950;
  }

  .eng-journey-empty p,
  .eng-journey-loader p {
    color: #64748b;
    font-size: 0.82rem;
    line-height: 1.48;
    font-weight: 760;
    max-width: 430px;
    margin: 0 0 8px;
  }

  .eng-journey-dots {
    display: flex;
    gap: 8px;
    margin-bottom: 4px;
  }

  .eng-journey-dots span {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #4f46e5;
    animation: eng-journey-bounce 0.58s infinite alternate;
  }

  .eng-journey-dots span:nth-child(2) { animation-delay: 0.1s; }
  .eng-journey-dots span:nth-child(3) { animation-delay: 0.2s; }
  .eng-journey-dots span:nth-child(4) { animation-delay: 0.3s; }

  @keyframes eng-journey-bounce {
    to { transform: translateY(-10px); }
  }

  .eng-journey-delete-root {
    position: fixed;
    inset: 0;
    z-index: 2147483000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 18px;
    background: rgba(15, 23, 42, 0.10);
    backdrop-filter: blur(1.5px);
    -webkit-backdrop-filter: blur(1.5px);
  }

  .eng-journey-delete-backdrop {
    position: absolute;
    inset: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
  }

  .eng-journey-delete-card {
    position: relative;
    width: min(440px, 100%);
    background: #ffffff;
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-radius: 24px;
    box-shadow: 0 24px 70px rgba(17, 24, 39, 0.22);
    padding: 22px;
    display: grid;
    gap: 14px;
  }

  .eng-journey-delete-icon {
    width: 54px;
    height: 54px;
    border-radius: 20px;
    background: #fef2f2;
    color: #ef4444;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.15rem;
  }

  .eng-journey-delete-copy strong {
    color: #111827;
    display: block;
    font-size: 1.08rem;
    font-weight: 950;
  }

  .eng-journey-delete-copy p {
    color: #64748b;
    font-size: 0.82rem;
    line-height: 1.48;
    font-weight: 760;
    margin: 6px 0 0;
  }

  .eng-journey-delete-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
  }

  @media (max-width: 1180px) {
    .eng-journey-stat-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .eng-journey-toolbar {
      grid-template-columns: 1fr;
    }

    .eng-journey-filter-group {
      justify-content: flex-start;
    }
  }

  @media (max-width: 720px) {
    .eng-journey-root {
      padding: 12px 10px 22px;
    }

    .eng-journey-hero-actions,
    .eng-journey-filter-group {
      width: 100%;
    }

    .eng-journey-primary-btn,
    .eng-journey-soft-btn,
    .eng-journey-danger-btn,
    .eng-journey-filter-group select,
    .eng-journey-clear-btn {
      width: 100%;
    }

    .eng-journey-stat-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .eng-journey-delete-actions {
      display: grid;
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 520px) {
    .eng-journey-stat-grid,
    .eng-journey-grid {
      grid-template-columns: 1fr;
    }
  }
`;

export default JourneyList;
